import { useState } from "react";
import {
  CanAccess,
  EditBase,
  FieldTitle,
  Form,
  useGetList,
  useInput,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { Link, Navigate } from "react-router";
import { BooleanInput } from "@/components/admin/boolean-input";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { SelectInput } from "@/components/admin/select-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { FormToolbar } from "../layout/FormToolbar";
import type { AlertRule, DealActivityType, DealStageConfig } from "../types";
import {
  buildActivityChoices,
  buildStageChoices,
  resolveAlertRuleDescription,
  resolveStagesWithTargetTime,
  toAlertRuleErrorMessage,
  validateAppointmentActivityTypeId,
  validateHoursThreshold,
  validateStuckLeadCountThreshold,
  validateTargetStageKey,
} from "./alertRuleForm";

/**
 * Almikia Epic ER (TASK-006): a single alert rule's configuration --
 * direccion toggles it on/off and edits the parameters its `rule_type` uses
 * (`key`/`rule_type`/`label` are never editable here: the catalogue is fixed
 * by design, TASK-003's ADR). Gated the same way as the list screen so a
 * non-direccion role can't reach an editable form by typing the URL
 * directly -- mirrors `DealStageConfigEdit.tsx` / `DealActivityTypeEdit.tsx`.
 */
export const AlertRuleEdit = () => {
  const notify = useNotify();
  return (
    <CanAccess
      resource="alert_rules"
      action="edit"
      accessDenied={<Navigate to="/" replace />}
    >
      <EditBase
        redirect="list"
        mutationOptions={{
          onError: (error) =>
            notify(toAlertRuleErrorMessage(error), { type: "error" }),
        }}
      >
        <AlertRuleEditForm />
      </EditBase>
    </CanAccess>
  );
};

const AlertRuleEditForm = () => {
  const translate = useTranslate();
  const record = useRecordContext<AlertRule>();
  const { data: stageConfigs, isPending: stageConfigsPending } =
    useGetList<DealStageConfig>("deal_stage_configs", {
      pagination: { page: 1, perPage: 100 },
    });
  const { data: activityTypes, isPending: activityTypesPending } =
    useGetList<DealActivityType>("deal_activity_types", {
      pagination: { page: 1, perPage: 200 },
    });

  if (!record || stageConfigsPending || activityTypesPending) return null;
  if (!stageConfigs || !activityTypes) return null;

  const stageLabelByKey = new Map(
    stageConfigs.map((stage) => [stage.key, stage.label]),
  );
  const activityNameById = new Map(
    activityTypes.map((activity) => [activity.id, activity.name]),
  );
  const description = resolveAlertRuleDescription(
    record,
    stageLabelByKey,
    activityNameById,
  );

  return (
    <div className="mt-2 max-w-lg mx-auto flex flex-col gap-4 pb-8">
      <Card>
        <CardContent className="space-y-4 pt-4">
          <div>
            <h1 className="text-lg font-semibold">{record.label}</h1>
            <p className="text-sm text-muted-foreground">
              {translate(description.key, description.params)}
            </p>
          </div>
          <Form className="space-y-4">
            <BooleanInput
              source="enabled"
              label="resources.alert_rules.fields.enabled"
            />

            {record.rule_type === "sin_seguimiento" ||
            record.rule_type === "alta_intencion_sin_actividad" ? (
              <HoursThresholdInput source="hours_threshold" />
            ) : null}

            {record.rule_type === "detenido_en_etapa" ? (
              <StuckLeadCountInput source="stuck_lead_count_threshold" />
            ) : null}

            {record.rule_type === "cumple_criterio_sin_cita" ? (
              <>
                <SelectInput
                  source="target_stage_key"
                  label="resources.alert_rules.fields.target_stage_key"
                  choices={buildStageChoices(stageConfigs)}
                  validate={validateTargetStageKey}
                />
                <SelectInput
                  source="appointment_activity_type_id"
                  label="resources.alert_rules.fields.appointment_activity_type_id"
                  choices={buildActivityChoices(activityTypes)}
                  validate={validateAppointmentActivityTypeId}
                />
              </>
            ) : null}

            <FormToolbar />
          </Form>
        </CardContent>
      </Card>

      {record.rule_type === "detenido_en_etapa" ? (
        <StagesWithTargetTimeInfo stageConfigs={stageConfigs} />
      ) : null}
    </div>
  );
};

/**
 * `hours_threshold` (`sin_seguimiento` / `alta_intencion_sin_actividad`): a
 * required 1-2160 integer. Hand-rolled like `AttemptCapInput.tsx` /
 * `TargetTimeInput.tsx` so a cleared field stays a distinguishable "empty"
 * (never silently coerced to `0`) while `validateHoursThreshold` still runs.
 */
const HoursThresholdInput = ({ source }: { source: string }) => {
  const { id, field, isRequired } = useInput({
    source,
    validate: validateHoursThreshold,
  });
  const [displayValue, setDisplayValue] = useState<string>(
    field.value != null ? String(field.value) : "",
  );

  const handleChange = (nextValue: string) => {
    setDisplayValue(nextValue);
    field.onChange(nextValue === "" ? null : Number(nextValue));
  };

  return (
    <FormField id={id} name={field.name}>
      <FormLabel>
        <FieldTitle
          label="resources.alert_rules.fields.hours_threshold"
          source={source}
          isRequired={isRequired}
        />
      </FormLabel>
      <FormControl>
        <Input
          type="number"
          className="w-24 tabular-nums"
          value={displayValue}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={field.onBlur}
        />
      </FormControl>
      <FormError />
    </FormField>
  );
};

/**
 * `stuck_lead_count_threshold` (`detenido_en_etapa`): a required 1-100
 * integer, same hand-rolled shape as {@link HoursThresholdInput}.
 */
const StuckLeadCountInput = ({ source }: { source: string }) => {
  const { id, field, isRequired } = useInput({
    source,
    validate: validateStuckLeadCountThreshold,
  });
  const [displayValue, setDisplayValue] = useState<string>(
    field.value != null ? String(field.value) : "",
  );

  const handleChange = (nextValue: string) => {
    setDisplayValue(nextValue);
    field.onChange(nextValue === "" ? null : Number(nextValue));
  };

  return (
    <FormField id={id} name={field.name}>
      <FormLabel>
        <FieldTitle
          label="resources.alert_rules.fields.stuck_lead_count_threshold"
          source={source}
          isRequired={isRequired}
        />
      </FormLabel>
      <FormControl>
        <Input
          type="number"
          className="w-24 tabular-nums"
          value={displayValue}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={field.onBlur}
        />
      </FormControl>
      <FormError />
    </FormField>
  );
};

/**
 * Acceptance criterion (TASK-006): for "Detenido en etapa", the stages that
 * currently have a target time (read from EP) -- the rule applies only to
 * those, with a link to "Etapas".
 */
const StagesWithTargetTimeInfo = ({
  stageConfigs,
}: {
  stageConfigs: DealStageConfig[];
}) => {
  const translate = useTranslate();
  const stages = resolveStagesWithTargetTime(stageConfigs);

  return (
    <Card>
      <CardContent className="space-y-2 pt-4">
        <h3 className="text-sm font-medium text-muted-foreground">
          {translate("resources.alert_rules.detenido_en_etapa.title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {stages.length > 0
            ? stages.map((stage) => stage.label).join(", ")
            : translate("resources.alert_rules.detenido_en_etapa.empty")}
        </p>
        <p className="text-xs text-muted-foreground">
          {translate("resources.alert_rules.detenido_en_etapa.help")}
        </p>
        <Button asChild variant="outline" size="sm" className="min-h-11">
          <Link to="/deal_stage_configs">
            {translate("resources.alert_rules.detenido_en_etapa.link")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
